import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye, Globe, Mail, Users, Server, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { usePageMeta } from '../lib/usePageMeta';

export default function PrivacyPolicyPage() {
  const navigate = useNavigate();
  usePageMeta({
    title: 'Privacy Policy - Wricoo',
    description: 'Learn how Wricoo collects, uses, and protects your personal information.',
    robots: 'index, follow',
    canonical: 'https://wricoo.com/privacy-policy/',
  });

  return (
    <div className="min-h-screen bg-white" data-testid="privacy-policy-page">

      {/* Header */}
      <header className="border-b border-blue-200 bg-blue-600 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-blue-200 hover:text-white transition"><ArrowLeft className="w-5 h-5" /></Link>
            <Link to="/" className="text-xl font-bold text-white tracking-tight">Wricoo</Link>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="text-white hover:bg-blue-500 hover:text-white" onClick={() => navigate('/login/')}>Sign In</Button>
            <Button size="sm" className="bg-white text-blue-600 hover:bg-blue-50" onClick={() => navigate('/register/')}>Get Started</Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-b from-blue-50 to-white py-16 sm:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-100 mb-6">
            <Shield className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="font-bold text-gray-900 leading-tight mb-4">Privacy Policy</h1>
          <p className="text-gray-600 leading-relaxed">Your privacy matters. Here's how we protect it.</p>
          <p className="text-sm text-gray-400 mt-4">Last updated: February 14, 2026</p>
        </div>
      </section>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12 space-y-16">

        {/* Intro */}
        <section>
          <p className="text-gray-600 leading-relaxed mb-4">This Privacy Policy describes how Wricoo collects, uses, and protects your information when you use our Service. We are committed to transparency and comply with applicable privacy laws including:</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              'EU General Data Protection Regulation (GDPR)',
              'UK GDPR and Data Protection Act 2018',
              'California Consumer Privacy Act (CCPA/CPRA)',
              'California Online Privacy Protection Act (CalOPPA)',
              'India Digital Personal Data Protection Act, 2023',
              'Canada PIPEDA',
              'Australia Privacy Act 1988 (APPs)',
            ].map((law, i) => (
              <div key={i} className="flex items-center gap-2.5 text-sm text-gray-600 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
                <Globe className="w-4 h-4 text-blue-500 flex-shrink-0" />
                {law}
              </div>
            ))}
          </div>
        </section>

        {/* Personal Data */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-8 bg-blue-500 rounded-full" />
            <h2 className="font-bold text-gray-900">Personal Data We Collect</h2>
          </div>
          <div className="space-y-4">
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <Users className="w-5 h-5 text-indigo-500" />
                <h3 className="text-base font-semibold text-gray-900">Account Information</h3>
              </div>
              <ul className="space-y-2">
                {['Email address', 'First name and last name', 'Account login credentials', 'Billing information (via Razorpay)'].map((item, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-sm text-gray-600">
                    <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" />{item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <Eye className="w-5 h-5 text-violet-500" />
                <h3 className="text-base font-semibold text-gray-900">Usage Data</h3>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">IP address, browser type, device type, pages visited, time and date of visit, session duration, and diagnostic data.</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-amber-800 mb-1">Important</p>
                  <p className="text-sm text-amber-700">We do not store full payment card details. All payments are securely processed by Razorpay.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Tracking */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-8 bg-indigo-500 rounded-full" />
            <h2 className="font-bold text-gray-900">Tracking & Analytics</h2>
          </div>
          <p className="text-gray-600 leading-relaxed mb-4">We use cookies and similar technologies for analytics purposes:</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="bg-white border border-gray-200 rounded-xl p-5 hover:border-blue-200 transition-all">
              <h3 className="text-base font-semibold text-gray-900 mb-2">Google Analytics 4</h3>
              <p className="text-sm text-gray-600">For understanding aggregated user behavior patterns.</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-5 hover:border-blue-200 transition-all">
              <h3 className="text-base font-semibold text-gray-900 mb-2">Microsoft Clarity</h3>
              <p className="text-sm text-gray-600">For behavioral analytics including heatmaps and session recordings.</p>
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-4">These tools are configured to avoid collecting directly identifiable personal information.</p>
        </section>

        {/* Legal Basis */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-8 bg-violet-500 rounded-full" />
            <h2 className="font-bold text-gray-900">Legal Basis for Processing</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { title: 'Contractual Necessity', desc: 'To provide the SaaS platform and subscription services.', color: 'blue' },
              { title: 'Legitimate Interests', desc: 'To improve the Service, prevent fraud, and secure our platform.', color: 'indigo' },
              { title: 'Consent', desc: 'For marketing communications and non-essential cookies.', color: 'violet' },
              { title: 'Legal Obligation', desc: 'To comply with tax, financial, and regulatory requirements.', color: 'emerald' },
            ].map((item, i) => (
              <div key={i} className={`bg-${item.color}-50 border border-${item.color}-100 rounded-xl p-5`}>
                <h3 className="text-base font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* How We Use Data */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-8 bg-emerald-500 rounded-full" />
            <h2 className="font-bold text-gray-900">How We Use Your Data</h2>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
            <ul className="space-y-2">
              {[
                'Provide and maintain the Service',
                'Manage subscriptions and billing',
                'Provide customer support',
                'Improve user experience',
                'Monitor fraud and platform abuse',
                'Comply with legal obligations',
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-2.5 text-sm text-gray-600">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />{item}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Your Rights */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-8 bg-orange-500 rounded-full" />
            <h2 className="font-bold text-gray-900">Your Privacy Rights</h2>
          </div>
          <div className="space-y-4">
            {[
              {
                region: 'EEA / UK Users (GDPR)',
                rights: ['Access Your data', 'Correct inaccurate data', 'Request deletion', 'Restrict processing', 'Object to processing', 'Data portability', 'Withdraw consent'],
                color: 'blue',
              },
              {
                region: 'California Residents (CCPA/CPRA)',
                rights: ['Know what Personal Information we collect', 'Request deletion', 'Correct inaccurate information', 'Opt out of sale or sharing', 'Non-discrimination for exercising rights'],
                color: 'indigo',
              },
              {
                region: 'India (DPDP Act 2023)',
                rights: ['Access Your data', 'Request correction and erasure', 'Withdraw consent', 'Grievance redressal'],
                color: 'violet',
              },
            ].map((section, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-xl p-6 hover:border-blue-200 transition-all">
                <h3 className="text-base font-semibold text-gray-900 mb-3">{section.region}</h3>
                <ul className="space-y-1.5">
                  {section.rights.map((right, j) => (
                    <li key={j} className="flex items-center gap-2.5 text-sm text-gray-600">
                      <span className={`w-1.5 h-1.5 rounded-full bg-${section.color}-400 flex-shrink-0`} />{right}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p className="text-sm text-gray-500 mt-4">We do not sell Personal Information. California residents may contact us to opt out of sharing.</p>
        </section>

        {/* Data Security */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-8 bg-green-500 rounded-full" />
            <h2 className="font-bold text-gray-900">Data Security</h2>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { icon: Lock, text: 'Encryption in transit (HTTPS)' },
              { icon: Shield, text: 'Access control mechanisms' },
              { icon: Users, text: 'Limited internal access' },
              { icon: Server, text: 'Periodic security review' },
            ].map((item, i) => (
              <div key={i} className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
                <item.icon className="w-5 h-5 text-green-600 flex-shrink-0" />
                <span className="text-sm text-green-800">{item.text}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Other Sections */}
        <section className="space-y-8">
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-3">International Data Transfers</h3>
            <p className="text-sm text-gray-600 leading-relaxed">Your data is primarily processed in India. If transferred outside the EEA/UK, we implement appropriate safeguards such as Standard Contractual Clauses (SCCs).</p>
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-3">Data Retention</h3>
            <p className="text-sm text-gray-600 leading-relaxed">We retain data only as long as necessary for contractual, legal, or legitimate business purposes.</p>
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-3">Children's Privacy</h3>
            <p className="text-sm text-gray-600 leading-relaxed">Our Service is not intended for individuals under 16. We do not knowingly collect data from minors.</p>
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-3">Changes to This Policy</h3>
            <p className="text-sm text-gray-600 leading-relaxed">We may update this Policy periodically. Material changes will be notified via email or platform notice.</p>
          </div>
        </section>

        {/* Contact */}
        <section className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 rounded-2xl p-8 sm:p-10">
          <div className="flex items-center gap-3 mb-4">
            <Mail className="w-6 h-6 text-blue-600" />
            <h2 className="font-bold text-gray-900">Contact Us</h2>
          </div>
          <p className="text-gray-600 leading-relaxed mb-4">If you have any questions about this Privacy Policy or want to exercise your rights, please contact us:</p>
          <a href="mailto:contact@wricoo.com" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium transition">
            contact@wricoo.com
          </a>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-10 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap items-center justify-between gap-4">
          <p className="text-sm text-gray-500">&copy; {new Date().getFullYear()} Wricoo. All rights reserved.</p>
          <nav className="flex gap-4 text-sm text-gray-500">
            <a href="/about/" className="hover:text-gray-900 transition">About</a>
            <a href="/pricing/" className="hover:text-gray-900 transition">Pricing</a>
            <a href="/tools/" className="hover:text-gray-900 transition">Tools</a>
            <a href="/privacy-policy/" className="hover:text-gray-900 transition">Privacy</a>
            <a href="/terms-and-conditions/" className="hover:text-gray-900 transition">Terms</a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
